/**
 * Reasoning
 *
 * Collapsible display for streamed model reasoning summaries.
 *
 * Primary responsibilities:
 * - Auto-open while reasoning streams and close when it finishes
 * - Grow with live thought text instead of clipping to the first open height
 * - Render reasoning text without stream-in animation that hides short thoughts
 *
 * Dependencies:
 * - Radix Collapsible
 * - Streamdown
 */

'use client'

import {
  createContext,
  type ComponentProps,
  type ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { Brain, ChevronDown } from 'lucide-react'
import { Streamdown } from 'streamdown'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import { useControllableState } from '@/components/ai-elements/use-controllable-state'
import { Shimmer } from '@/components/ai-elements/shimmer'
import { useInteractionPreferences } from '@/hooks/useInteractionPreferences'

interface ReasoningContextValue {
  isStreaming: boolean
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  duration?: number
}

const ReasoningContext = createContext<ReasoningContextValue | null>(null)

export function useReasoning() {
  const context = useContext(ReasoningContext)

  if (!context) {
    throw new Error('Reasoning components must be used within Reasoning')
  }

  return context
}

export interface ReasoningProps extends ComponentProps<typeof Collapsible> {
  autoCloseOnEnd?: boolean
  isStreaming?: boolean
  duration?: number
}

export function Reasoning({
  autoCloseOnEnd = true,
  className,
  isStreaming = false,
  duration,
  open,
  defaultOpen = true,
  onOpenChange,
  children,
  ...props
}: ReasoningProps) {
  const [liveDuration, setLiveDuration] = useState(0)
  const wasStreamingRef = useRef(false)
  const { prefersAlwaysExplain } = useInteractionPreferences()
  // Human-Centered AI: when the user has chosen "always show reasoning", never
  // auto-collapse it after streaming ends. Defaults are untouched otherwise.
  const effectiveAutoCloseOnEnd = autoCloseOnEnd && !prefersAlwaysExplain
  const [isOpen, setIsOpen] = useControllableState({
    prop: open,
    defaultProp: defaultOpen,
    onChange: onOpenChange
  })

  useEffect(() => {
    if (!isStreaming) {
      if (effectiveAutoCloseOnEnd && wasStreamingRef.current) {
        setIsOpen(false)
      }

      wasStreamingRef.current = false
      return
    }

    wasStreamingRef.current = true
    setIsOpen(true)

    const startedAt = Date.now()
    setLiveDuration(0)
    const interval = window.setInterval(() => {
      setLiveDuration(Math.max(0, Math.round((Date.now() - startedAt) / 1000)))
    }, 1000)

    return () => window.clearInterval(interval)
  }, [effectiveAutoCloseOnEnd, isStreaming, setIsOpen])

  const contextValue = useMemo(
    () => ({
      isStreaming,
      isOpen,
      setIsOpen,
      duration: duration ?? liveDuration
    }),
    [duration, isOpen, isStreaming, liveDuration, setIsOpen]
  )

  return (
    <ReasoningContext.Provider value={contextValue}>
      <Collapsible
        className={cn(
          'not-prose text-muted-foreground w-full',
          className
        )}
        open={isOpen}
        onOpenChange={setIsOpen}
        {...props}
      >
        {children}
      </Collapsible>
    </ReasoningContext.Provider>
  )
}

export interface ReasoningTriggerProps extends ComponentProps<typeof CollapsibleTrigger> {
  getThinkingMessage?: (isStreaming: boolean, duration?: number) => ReactNode
}

export function ReasoningTrigger({
  className,
  children,
  getThinkingMessage,
  ...props
}: ReasoningTriggerProps) {
  const { isStreaming, isOpen, duration } = useReasoning()
  const label = getThinkingMessage
    ? getThinkingMessage(isStreaming, duration)
    : isStreaming
      ? 'Thinking...'
      : typeof duration === 'number' && duration > 0
        ? `Thought for ${duration}s`
        : 'Thought'

  return (
    <CollapsibleTrigger
      className={cn(
        'text-muted-foreground hover:text-foreground focus-visible:ring-ring flex w-full items-center gap-2 text-left text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2',
        className
      )}
      {...props}
    >
      <span className="flex min-w-0 items-center gap-2">
        <span
          className={cn(
            'text-muted-foreground inline-flex size-4 items-center justify-center',
            isStreaming && 'text-primary'
          )}
        >
          <Brain className="size-3.5" />
        </span>
        <Shimmer active={isStreaming} className="min-w-0">
          {children ?? label}
        </Shimmer>
      </span>
      <ChevronDown
        className={cn(
          'size-3.5 shrink-0 transition-transform',
          // Collapsed points right (forward = "click to open"), open points
          // down. Avoids the false affordance of a down chevron appearing to
          // expand an unrelated annotation directly above the pill (UI-01).
          isOpen ? 'rotate-0' : '-rotate-90'
        )}
      />
    </CollapsibleTrigger>
  )
}

export interface ReasoningContentProps extends ComponentProps<typeof CollapsibleContent> {
  /**
   * Kept for API compatibility. Reasoning never uses Streamdown blur-in —
   * short thoughts were clipped to the first couple of words until the turn
   * finished remounting the static renderer.
   */
  animateStreaming?: boolean
  children: string
}

export function ReasoningContent({
  animateStreaming: _animateStreaming = false,
  className,
  children,
  ...props
}: ReasoningContentProps) {
  const { isStreaming, isOpen } = useReasoning()
  const contentRef = useRef<HTMLDivElement | null>(null)

  // Radix locks `--radix-collapsible-content-height` to the height measured
  // when the panel first opens. Short reasoning opens on the first tokens,
  // then grows — without refreshing that height, overflow clips to ~2 words
  // until streaming ends and the panel remounts.
  useLayoutEffect(() => {
    if (!isOpen) return
    const node = contentRef.current
    if (!node) return

    const syncHeight = () => {
      const nextHeight = Math.max(node.scrollHeight, node.offsetHeight)
      if (nextHeight > 0) {
        node.style.setProperty(
          '--radix-collapsible-content-height',
          `${nextHeight}px`
        )
      }
      if (isStreaming) {
        node.style.height = 'auto'
      }
    }

    syncHeight()

    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => {
      syncHeight()
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [children, isOpen, isStreaming])

  return (
    <CollapsibleContent
      ref={contentRef}
      className={cn(
        'text-muted-foreground mt-2 pl-6 pr-2 text-sm',
        // While streaming, skip height-lock animations so growing thoughts
        // are never clipped. Collapse animation only applies when closed.
        isStreaming
          ? 'overflow-visible data-[state=open]:animate-none'
          : 'overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down',
        className
      )}
      {...props}
    >
      <Streamdown
        className="prose prose-sm text-muted-foreground dark:prose-invert prose-headings:my-1 prose-headings:text-muted-foreground prose-p:my-2 prose-p:text-muted-foreground prose-ol:my-2 prose-ol:text-muted-foreground prose-ul:my-2 prose-ul:text-muted-foreground prose-li:my-0 prose-li:text-muted-foreground prose-strong:text-muted-foreground prose-em:text-muted-foreground prose-code:text-muted-foreground prose-blockquote:text-muted-foreground max-w-none min-w-0 [&_*]:!text-muted-foreground prose-code:before:content-none prose-code:after:content-none [&_:not(pre)>code]:inline [&_:not(pre)>code]:max-w-full [&_:not(pre)>code]:whitespace-break-spaces [&_:not(pre)>code]:break-words"
        mode={isStreaming ? 'streaming' : 'static'}
        parseIncompleteMarkdown={isStreaming}
        isAnimating={false}
      >
        {children}
      </Streamdown>
    </CollapsibleContent>
  )
}
